import { getLang } from '../i18n'
import { getSettings } from './store'

/**
 * Spoken guidance uses the platform's own speech synthesis. Same reason as the
 * audio engine: it works offline, costs nothing, and needs no account.
 *
 * Persian voices are not installed on every device. When one is missing we stay
 * silent rather than reading Persian text with an English voice, which is worse
 * than no voice at all at 3am.
 */

let voices: SpeechSynthesisVoice[] = []

function refreshVoices(): void {
  if (!('speechSynthesis' in window)) return
  voices = window.speechSynthesis.getVoices()
}

if ('speechSynthesis' in window) {
  refreshVoices()
  window.speechSynthesis.addEventListener('voiceschanged', refreshVoices)
}

function pickVoice(langTag: string): SpeechSynthesisVoice | undefined {
  if (!voices.length) refreshVoices()
  const base = langTag.split('-')[0]!
  return (
    voices.find((v) => v.lang.toLowerCase() === langTag.toLowerCase()) ??
    voices.find((v) => v.lang.toLowerCase().startsWith(base))
  )
}

export function voiceAvailable(): boolean {
  if (!('speechSynthesis' in window)) return false
  return Boolean(pickVoice(getLang() === 'fa' ? 'fa-IR' : 'en-US'))
}

export interface SpeakOptions {
  rate?: number
  pitch?: number
  volume?: number
  /** Speak even if the user turned voice guidance off (used for nothing yet — kept explicit). */
  force?: boolean
}

export function speak(text: string, opts: SpeakOptions = {}): Promise<void> {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) return resolve()
    if (!opts.force && !getSettings().voiceGuidance) return resolve()

    const langTag = getLang() === 'fa' ? 'fa-IR' : 'en-US'
    const voice = pickVoice(langTag)
    if (!voice) return resolve() // no matching voice: stay quiet

    const u = new SpeechSynthesisUtterance(text)
    u.voice = voice
    u.lang = voice.lang
    // Slower and lower than default. Default TTS cadence sounds like an
    // announcement; this needs to sound like someone sitting next to you.
    u.rate = opts.rate ?? 0.78
    u.pitch = opts.pitch ?? 0.9
    u.volume = opts.volume ?? 0.85
    u.onend = () => resolve()
    u.onerror = () => resolve()
    window.speechSynthesis.speak(u)
  })
}

export function shutUp(): void {
  if ('speechSynthesis' in window) window.speechSynthesis.cancel()
}
